import React, { ComponentType } from 'react';
import { CardComponentRegistry as ICardComponentRegistry, CardSectionProps, OperationalMode, Environment } from '../../../types/compositeCards';
import { CardHeader, CardContent, CardActions, CardMetadata } from '../BaseComponents';

// Import actual component implementations as they are created
import { ArtifactDetails } from './ArtifactComponents';
import { ArtifactActionComponent } from './ArtifactActionComponent';
import { ArtifactList } from './ListComponents';
import { AssessmentRankDisplay } from './AssessmentRankDisplay';
import { ArtifactListDisplay } from './ArtifactListDisplay';
import { ValueGenerationDisplay } from './ValueGenerationDisplay';
import { WorkflowStatusComponent } from './WorkflowStatusComponent';
import { ArtifactSkillsCard } from '../ContentCards/ArtifactSkillsCard';
import { ProcessSkillsCard } from '../ContentCards/ProcessSkillsCard';
import { PrimeArtifactCard } from '../ContentCards/PrimeArtifactCard';
import { ValueMetricsCard } from '../ContentCards/ValueMetricsCard';
import { ObjectiveCard } from '../ContentCards/ObjectiveCard';
import { StrategyCard } from '../ContentCards/StrategyCard';
import { OutcomeCard } from '../ContentCards/OutcomeCard';
import { PrimeStatesContextCard, PrimeTransitionsContextCard, PrimeProcessesContextCard } from '../ContextCards';
import {
  ArtifactItemState,
  ArtifactWorkflowProgress,
  ItemsRequiringAttention,
} from './ArtifactItemComponents';
import { ProcessFlowCard, ProcessFlowExecutionStatus } from './ProcessFlowComponents';

// Import Dashboard Reporting Components
import { 
  ValueCreationComponent,
  ValueCreationDetailComponent,
  ProductCatalogComponent,
  ProductOperationsComponent,
  ProductDemandComponent,
  ProductSupplyComponent,
  ProductDeliveryComponent,
  ProductDevelopmentComponent,
  ProductProjectionsComponent,
  MarketFitComponent
} from '../../Dashboard/ReportingComponents';

// Placeholder component factory
const createPlaceholderComponent = (componentName: string): ComponentType<CardSectionProps> => {
  return (props: CardSectionProps) => React.createElement('div', {
    style: {
      padding: '12px',
      backgroundColor: '#fef3c7',
      border: '1px solid #f59e0b',
      borderRadius: '8px',
      margin: '8px 0',
    }
  }, [
    React.createElement('div', {
      key: 'header',
      style: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
      }
    }, [
      React.createElement('div', {
        key: 'icon',
        style: {
          width: '20px',
          height: '20px',
          backgroundColor: '#f59e0b',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '12px',
          fontWeight: 'bold',
        }
      }, 'TODO'),
      React.createElement('h4', {
        key: 'title',
        style: {
          margin: 0,
          color: '#92400e',
          fontSize: '14px',
          fontWeight: '500',
        }
      }, componentName)
    ]),
    React.createElement('p', {
      key: 'description',
      style: {
        margin: 0,
        color: '#92400e',
        fontSize: '12px',
        lineHeight: '1.4',
      }
    }, `This component needs to be implemented. It will handle the specific functionality for ${componentName}.`)
  ]);
};

class CardComponentRegistry implements ICardComponentRegistry {
  public baseComponents: ICardComponentRegistry['baseComponents'];
  public roleComponents: ICardComponentRegistry['roleComponents'];
  public modelComponents: ICardComponentRegistry['modelComponents'];
  public cardTypeComponents: ICardComponentRegistry['cardTypeComponents'];

  constructor() {
    this.baseComponents = {
      header: CardHeader as ComponentType<CardSectionProps>,
      content: CardContent as ComponentType<CardSectionProps>,
      actions: CardActions as ComponentType<CardSectionProps>,
      metadata: CardMetadata as ComponentType<CardSectionProps>,
      
      // Artifact Components
      'artifact-details': ArtifactDetails,
      'artifact-action': ArtifactActionComponent,
      'workflow-status': WorkflowStatusComponent,
      'artifact-development': createPlaceholderComponent('ArtifactDevelopment'),
      'artifact-state-transition-overview': createPlaceholderComponent('ArtifactStateTransitionOverview'),
      'artifact-state-list': createPlaceholderComponent('ArtifactStateList'),
      'artifact-transition-list': createPlaceholderComponent('ArtifactTransitionList'),
      'artifact-process-list': createPlaceholderComponent('ArtifactProcessList'),
      'artifact-child-list': createPlaceholderComponent('ArtifactChildList'),
      'artifact-state-artifactitems': createPlaceholderComponent('ArtifactStateArtifactItems'),
      'artifact-state-detail': createPlaceholderComponent('ArtifactStateDetail'),
      'artifact-transition-detail': createPlaceholderComponent('ArtifactTransitionDetail'),
      
      // Assessment and Value Components
      'assessment-rank': AssessmentRankDisplay,
      'child-artifact-list': ArtifactListDisplay,
      'value-generation': ValueGenerationDisplay,
      'value-metrics': ValueMetricsCard,
      'objective': ObjectiveCard,
      'strategy': StrategyCard,
      'outcome': OutcomeCard,
      
      // Process Components
      'process-detail': createPlaceholderComponent('ProcessDetail'),
      'process-control-detail': createPlaceholderComponent('ProcessControlDetail'),
      'process-artifacts': createPlaceholderComponent('ProcessArtifacts'),
      'process-artifactitems': createPlaceholderComponent('ProcessArtifactItems'),
      'process-flow': ProcessFlowCard,
      'process-flow-execution-status': ProcessFlowExecutionStatus,
      'process-actions': createPlaceholderComponent('ProcessActions'),
      'process-development': createPlaceholderComponent('ProcessDevelopment'),
      'process-resource-assignment': createPlaceholderComponent('ProcessResourceAssignment'),
      
      // Dashboard Reporting Components
      'value-creation': ValueCreationComponent as unknown as ComponentType<CardSectionProps>,
      'value-creation-detail': ValueCreationDetailComponent as unknown as ComponentType<CardSectionProps>,
      'product-catalog': ProductCatalogComponent as unknown as ComponentType<CardSectionProps>,
      'product-operations': ProductOperationsComponent as unknown as ComponentType<CardSectionProps>,
      'product-demand': ProductDemandComponent as unknown as ComponentType<CardSectionProps>,
      'product-supply': ProductSupplyComponent as unknown as ComponentType<CardSectionProps>,
      'product-delivery': ProductDeliveryComponent as unknown as ComponentType<CardSectionProps>,
      'product-development': ProductDevelopmentComponent as unknown as ComponentType<CardSectionProps>,
      'product-projections': ProductProjectionsComponent as unknown as ComponentType<CardSectionProps>,
      'market-fit': MarketFitComponent as unknown as ComponentType<CardSectionProps>,
      
      // Skill Components
      'artifact-skills': ArtifactSkillsCard,
      'process-skills': ProcessSkillsCard,
      
      // Artifact Item Components
      'artifact-item-detail': createPlaceholderComponent('ArtifactItemDetail'),
      'artifact-item-parameters': createPlaceholderComponent('ArtifactItemParameters'),
      'artifact-item-action': createPlaceholderComponent('ArtifactItemAction'),
      'artifact-item-state': ArtifactItemState,
      'artifact-workflow-progress': ArtifactWorkflowProgress,
      'items-requiring-attention': ItemsRequiringAttention,
      
      // List Components
      'artifact-list': ArtifactList,
      'process-list': createPlaceholderComponent('ProcessList'),
      'artifact-item-list': createPlaceholderComponent('ArtifactItemList'),
      
      // Role-specific Components
      'architect-controls': createPlaceholderComponent('ArchitectControls'),
      'operator-monitoring': createPlaceholderComponent('OperatorMonitoring'),
      'coach-insights': createPlaceholderComponent('CoachInsights'),
      
      // Model-specific Components
      'prime-development': createPlaceholderComponent('PrimeDevelopment'),
      'company-operations': createPlaceholderComponent('CompanyOperations'),
      'sandbox-experiments': createPlaceholderComponent('SandboxExperiments'),
    };

    this.roleComponents = {
      architect: {
        'artifact-development': createPlaceholderComponent('ArchitectArtifactDevelopment'),
        'process-development': createPlaceholderComponent('ArchitectProcessDevelopment'),
      },
      operator: {
        'process-actions': createPlaceholderComponent('OperatorProcessActions'),
        'process-resource-assignment': createPlaceholderComponent('OperatorProcessResourceAssignment'),
      },
      coach: {
        // Coach-specific components
      },
      integrator: {
        // Integrator-specific components
      },
      development: {
        // Development-specific components
      },
      marketfit: {
        // Marketfit-specific components
      },
    };

    this.modelComponents = {
      Prime: {
        'artifact-state-transition-overview': createPlaceholderComponent('PrimeArtifactStateTransitionOverview'),
        'artifact-state-detail': createPlaceholderComponent('PrimeArtifactStateDetail'),
        'artifact-transition-detail': createPlaceholderComponent('PrimeArtifactTransitionDetail'),
        'process-detail': createPlaceholderComponent('PrimeProcessDetail'),
        'process-control-detail': createPlaceholderComponent('PrimeProcessControlDetail'),
      },
      Company: {
        // Company-specific components
      },
      Sandbox: {
        // Sandbox-specific components
      },
    };

    this.cardTypeComponents = {
      artifact: {
        'artifact-details': ArtifactDetails,
        'artifact-development': createPlaceholderComponent('ArtifactDevelopment'),
        'artifact-state-transition-overview': createPlaceholderComponent('ArtifactStateTransitionOverview'),
        'artifact-state-list': createPlaceholderComponent('ArtifactStateList'),
        'artifact-transition-list': createPlaceholderComponent('ArtifactTransitionList'),
        'artifact-process-list': createPlaceholderComponent('ArtifactProcessList'),
        'artifact-child-list': createPlaceholderComponent('ArtifactChildList'),
        'artifact-state-artifactitems': createPlaceholderComponent('ArtifactStateArtifactItems'),
      },
      prime_artifact: {
        content: PrimeArtifactCard,
        header: CardHeader as ComponentType<CardSectionProps>,
      },
      process: {
        'process-detail': createPlaceholderComponent('ProcessDetail'),
        'process-control-detail': createPlaceholderComponent('ProcessControlDetail'),
        'process-artifacts': createPlaceholderComponent('ProcessArtifacts'),
        'process-artifactitems': createPlaceholderComponent('ProcessArtifactItems'),
        'process-flow': createPlaceholderComponent('ProcessFlow'),
        'process-actions': createPlaceholderComponent('ProcessActions'),
        'process-development': createPlaceholderComponent('ProcessDevelopment'),
        'process-resource-assignment': createPlaceholderComponent('ProcessResourceAssignment'),
      },
      artifact_item: {
        'artifact-item-detail': createPlaceholderComponent('ArtifactItemDetail'),
        'artifact-item-parameters': createPlaceholderComponent('ArtifactItemParameters'),
        'artifact-item-action': createPlaceholderComponent('ArtifactItemAction'),
      },
      list: {
        'artifact-list': ArtifactList,
        'process-list': createPlaceholderComponent('ProcessList'),
        'artifact-item-list': createPlaceholderComponent('ArtifactItemList'),
        'architect-controls': createPlaceholderComponent('ArchitectControls'),
        'operator-monitoring': createPlaceholderComponent('OperatorMonitoring'),
        'coach-insights': createPlaceholderComponent('CoachInsights'),
        'prime-development': createPlaceholderComponent('PrimeDevelopment'),
        'company-operations': createPlaceholderComponent('CompanyOperations'),
        'sandbox-experiments': createPlaceholderComponent('SandboxExperiments'),
      },
      prime_states: {
        content: PrimeStatesContextCard,
      },
      prime_transitions: {
        content: PrimeTransitionsContextCard,
      },
      prime_processes: {
        content: PrimeProcessesContextCard,
      },
      prime_parameters: {
        content: createPlaceholderComponent('PrimeParametersContextCard'),
      },
      prime_skills: {
        content: createPlaceholderComponent('PrimeSkillsContextCard'),
      },
    };
  }

  registerBaseComponent(name: string, component: ComponentType<CardSectionProps>) {
    this.baseComponents[name] = component;
  }

  registerRoleComponent(role: OperationalMode, sectionType: string, component: ComponentType<CardSectionProps>) {
    if (!this.roleComponents[role]) {
      this.roleComponents[role] = {};
    }
    this.roleComponents[role][sectionType] = component;
  }

  registerModelComponent(model: Environment, sectionType: string, component: ComponentType<CardSectionProps>) {
    if (!this.modelComponents[model]) {
      this.modelComponents[model] = {};
    }
    this.modelComponents[model][sectionType] = component;
  }

  registerCardTypeComponent(cardType: string, sectionType: string, component: ComponentType<CardSectionProps>) {
    if (!this.cardTypeComponents[cardType]) {
      this.cardTypeComponents[cardType] = {};
    }
    this.cardTypeComponents[cardType][sectionType] = component;
  }

  getComponent(cardType: string, sectionType: string, uiRole: OperationalMode, modelType: Environment): ComponentType<CardSectionProps> | null {
    // 1. Check for card-type-specific component
    if (this.cardTypeComponents[cardType]?.[sectionType]) {
      return this.cardTypeComponents[cardType][sectionType];
    }

    // 2. Check for role-specific component
    if (this.roleComponents[uiRole]?.[sectionType]) {
      return this.roleComponents[uiRole][sectionType];
    }

    // 3. Check for model-specific component
    if (this.modelComponents[modelType]?.[sectionType]) {
      return this.modelComponents[modelType][sectionType];
    }

    // 4. Fallback to base component
    if (this.baseComponents[sectionType]) {
      return this.baseComponents[sectionType];
    }

    console.warn(`Component not found for cardType: ${cardType}, sectionType: ${sectionType}, role: ${uiRole}, model: ${modelType}`);
    return null;
  }

  getAllRegisteredCardTypes(): string[] {
    const types = new Set<string>();
    Object.keys(this.cardTypeComponents).forEach(type => types.add(type));
    // Add other known card types if they don't have specific components but should be discoverable
    types.add('artifact');
    types.add('process');
    types.add('artifact_item');
    types.add('list');
    types.add('prime_artifact');
    return Array.from(types);
  }
}

// Export singleton instance
export const cardComponentRegistry = new CardComponentRegistry();
export default cardComponentRegistry;